import { useCallback, useEffect, useRef } from 'react';
import config from '../config';
import useTraitSwapStore from '../hooks/useTraitSwapStore';
import { fetchImage, handleFetchImage } from '../utils/canvas';
import { getTraitImagePath } from '../utils/traits';
import Spinner from './Spinner';

const DEFAULT_WIDTH = 3000;
const DEFAULT_HEIGHT = 3000;

const TraitSwapPreviewImages = () => {
  return (
    <div className='grid grid-cols-2 gap-6'>
      <TraitSwapPreviewImage index={0} />
      <TraitSwapPreviewImage index={1} />
    </div>
  );
};

const TraitSwapPreviewImage = ({ index }) => {
  const canvasRef = useRef(null);
  const { selectedNfts, addFailedTraitImage, swappedLayers } = useTraitSwapStore();

  const getPropValue = useCallback(
    (property, _index) => {
      const nft = selectedNfts[_index];
      const metadata = nft.metadata;
      if (!metadata) return '';

      if (metadata.properties && typeof metadata.properties === 'object') {
        return metadata.properties[property] || '';
      }

      if (nft.attributes && Array.isArray(nft.attributes)) {
        const attr = nft.attributes.find((attr) => attr.trait_type === property);
        return attr ? attr.value || '' : '';
      }

      if (nft.properties && typeof nft.properties === 'object') {
        return nft.properties[property] || '';
      }

      return '';
    },
    [selectedNfts]
  );

  useEffect(() => {
    if (selectedNfts.length === 0) return;
    if (selectedNfts.filter((nft) => nft.metadata).length !== selectedNfts.length) return;

    const images = config.fourthLitterTraitLayers.map((property) => {
      const isSwappedLayer = swappedLayers.includes(property);
      const theIndex = isSwappedLayer ? (index === 0 ? 1 : 0) : index;
      const propValue = getPropValue(property, theIndex);
      const imageUrl = getTraitImagePath(property, propValue);

      return {
        src: imageUrl,
        x: 0,
        y: 0,
        width: DEFAULT_WIDTH,
        height: DEFAULT_HEIGHT,
      };
    });

    const canvasContext = canvasRef.current?.getContext('2d');
    if (!canvasContext) return;

    Promise.all(images.map(fetchImage)).then((results) =>
      results.forEach((result) => {
        if (result.errorMsg) {
          addFailedTraitImage(result.image.src);
        }
        handleFetchImage(canvasContext, result);
      })
    );
  }, [selectedNfts, index, swappedLayers]);

  return (
    <div className='relative aspect-square w-full max-w-[750px] overflow-hidden rounded-lg bg-gray-900'>
      <div className='absolute left-3 z-30 top-4 z-10 flex h-[32px] w-[32px] items-center justify-center rounded-full border-2 border-white bg-primary-500'>
        <p className='font-bold text-white'>{index + 1}</p>
      </div>
      <canvas ref={canvasRef} width={3000} height={3000} className='h-full w-full rounded-lg z-20 relative' />
      <div className='absolute left-0 top-0 z-10 w-full h-full bg-black/50 flex items-center justify-center'>
        <Spinner />
      </div>
    </div>
  );
};

export default TraitSwapPreviewImages;
